<template>
    <div class="content-wrapper">
        <!-- Page Loader -->
        <div class="vld-parent">
            <loading :active.sync="isLoading" :is-full-page="fullPage"></loading>
        </div>

        <breadcrumb :page="$t('form.paymentSummary.pendingPaymentSummaries')"></breadcrumb>

        <div class="content">
            <div class="container-fluid">

                <b-alert v-model="alert" :variant="alertClass" dismissible fade>{{alertMessage}}</b-alert>

                <!-- Filters -->
                <div class="card">
                    <div class="card-header bg-light">
                        <div class="card-title">{{$t('filters')}}</div>
                        <div class="text-right">
                            <b-button variant="primary" size="sm" @click="detailedSearch = !detailedSearch">
                                {{$t('button.detailedSearch')}}</b-button>
                            <b-button variant="default" size="sm" @click="clearSearch()">{{$t('button.resetFilter')}}
                            </b-button>
                        </div>
                    </div>
                    <div class="card-body">

                        <div class="row">
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label>{{$t('form.paymentSummary.searchCriteria')}}</label>
                                    <v-select @input="loadPreset" label="name" :options="presetList" v-model="preset">
                                    </v-select>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.paymentSummary.summaryNumber')}}</label>
                                    <input type="text" v-model="search.reference_number" @keyup="searchPaymentSummary"
                                        class="form-control" />
                                </div>
                            </div>

                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.paymentSummary.status')}}</label>
                                    <v-select @input="searchPaymentSummary" v-model="search.status" :options="statusArray">
                                    </v-select>
                                </div>
                            </div>

                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.paymentSummary.studentNo')}}</label>
                                    <input type="number" @keyup="searchPaymentSummary" v-model="search.studentNumber" class="form-control">
                                </div>
                            </div>

                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.paymentSummary.date')}}</label>
                                    <date-picker :lang="lang" class="date-picker" v-model="search.summary_date" valueType="format"
                                        :format="dateFormat" @change="searchPaymentSummary"
                                        :popup-style="{ top: '100%', left: 0}" :append-to-body="false">
                                    </date-picker>
                                </div>
                            </div>

                            <div class="col-sm-3">
                                <div class="form-group">
                                    <label>{{$t('form.paymentSummary.totalAmount')}}</label>
                                    <input type="text" v-model="search.total_amount" @keyup="searchPaymentSummary"
                                        class="form-control" />
                                </div>
                            </div>
                        </div>

                        <div class="row" v-if="detailedSearch">

                            <div class="col-12">
                                <hr>
                            </div>

                            <div class="col-sm-6">
                                <div class="form-group">
                                    <input type="text" v-model="preset.name" class="form-control"
                                        :placeholder="$t('form.book.presetName')" />
                                </div>
                            </div>

                            <div class="col-sm-6">
                                <button class="btn btn-primary" @click="savePreset">{{$t('button.save')}}</button>
                                <button class="btn btn-danger" v-show="presetSelected"
                                    @click="deletePreset">{{$t('button.delete')}}</button>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- List -->
                <div class="card">
                    <div class="card-header bg-light">
                        <div class="card-title">{{$t('form.paymentSummary.pendingPaymentSummaries')}}</div>
                    </div>
                    <div class="card-body table-responsive p-0">
                        <table class="table table-hover" v-if="paymentSummaries.length">
                            <thead>
                                <tr>
                                    <th><input type="checkbox" @change="toggleCheck" v-model="selectedPaymentSummaries" class="mt-1 ml-1"></th>
                                    <th>{{$t('form.paymentSummary.summaryNumber')}}</th>
                                    <th>{{$t('form.paymentSummary.status')}}</th>
                                    <th>{{$t('form.paymentSummary.studentNumber')}}</th>
                                    <th>{{$t('form.paymentSummary.date')}}</th>
                                    <th>{{$t('form.paymentSummary.totalAmount')}}</th>
                                    <th class="text-right">{{$t('form.paymentSummary.action')}}</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr @dblclick="goToEdit(paymentSummary.id)" v-for="(paymentSummary, index) in paymentSummaries" :key="index">
                                    <td><input type="checkbox" class="mt-1 ml-1" v-model="selectedPaymentSummaries" :value="paymentSummary"></td>
                                    <td>{{ paymentSummary.reference_number }}</td>
                                    <td>{{ paymentSummary.status }}</td>
                                    
                                    <td>
                                        <template>
                                            <router-link
                                                :to="{name: 'StudentEdit', params: {id: paymentSummary.student_number}}">
                                                {{ paymentSummary.student_number }}
                                            </router-link>
                                        </template>
                                    </td>
                                    <td>{{ paymentSummary.summary_date | formatDate(dateFormat) }}</td>
                                    <td>{{ paymentSummary.currency }} {{ paymentSummary.total_amount | curreny(decimalDigits, decimalPoint, thousandSeparator) }}</td>
                                    <td class="text-right">
                                        <button @click="goToShow(paymentSummary.id)" class="btn btn-dark btn-sm">
                                            <i class="fa fa-print"></i>
                                        </button>
                                        <button @click="goToEdit(paymentSummary.id)"
                                            class="btn btn-info btn-sm">
                                            <i class="fa fa-edit"></i>
                                        </button>
                                        <button class="btn btn-danger btn btn-sm" v-on:click="deletePaymentSummary(paymentSummary)">
                                            <span class="fa fa-trash"></span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p class="p-3 m-0" v-else>{{$t('noRecord')}}</p>
                    </div>

                    <div class="container-fluid" v-if="paymentSummaries.length">
                        <button @click="bulkApprove()"
                                class="btn btn-primary btn-sm mb-3" style="letter-spacing: 0.0625em;">
                                {{$t('form.paymentSummary.approvePaymentSummary')}}
                        </button>
                    </div>

                    <pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="5"
                        @paginate="paginate()">
                    </pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: ['permissions', 'dateFormat', 'decimalDigits', 'decimalPoint', 'thousandSeparator'],

        data() {
            return {

                alert: false,
                alertClass: '',
                alertMessage: '',

                statusArray: [],
                selectedPaymentSummaries: [],
                status: '',
                pending: 'Pending',

                lang: {
                    formatLocale: {
                        firstDayOfWeek: 1,
                    },
                },

                isLoading: false,
                fullPage: true,

                request_source: '',
                detailedSearch: false,
                presetSelected: false,

                pagination: {
                    current_page: 1
                },

                search: {
                    status: '',
                    reference_number: '',
                    studentNumber: ''
                },

                preset: {
                    name: '',
                    entity: 'pendingPaymentSummaries',
                    value: {}
                },

                currencies: [],
                presetList: [],
                paymentSummaries: [],
            };
        },

        async mounted() {
            this.isLoading = true;

            if (!this.permissions.viewInvoices) {
                this.$router.push("/");
                return;
            }

            await this.getCurrencies();

            if (localStorage.getItem('filter-payment-summary-pending')) {
                this.search = JSON.parse(localStorage.getItem('filter-payment-summary-pending'));
            }
            this.getAllStatus();
            this.getPaymentSummaries();
            this.getPresets();
        },

        watch: {
            search: {
                handler(newSearch) {
                    localStorage.setItem('filter-payment-summary-pending', JSON.stringify(this.search));

                    const emojiRegex = /[\u{10000}-\u{10FFFF}]/gu;
                    
                    // Loop through all properties in the search object
                    Object.keys(newSearch).forEach(key => {
                    // Only process string values
                    if (typeof newSearch[key] === 'string' && emojiRegex.test(newSearch[key])) {
                        // Strip the emoji and update the reactive property
                        this.search[key] = newSearch[key].replace(emojiRegex, '');
                    }
                    });
                },
                deep: true  
            }
        },

        methods: {
            /**
             * get all payment summary status from config
             */
            getAllStatus() {
                let self = this;
                return new Promise(function(resolve, reject) {
                axios.get('/api/config')
                .then(response => {
                if (response.data) {
                    let config = response.data;
                    self.status = config.payment_summary_status;
                    if (( typeof self.status == 'object') && (self.status != '')) {
                            self.statusArray = Object.values(self.status);
                        }
                    }
                })
                resolve('Successfully got config values');
                });
            },

            /**
             * Delete payment summary
             */
            deletePaymentSummary(paymentSummary) {

                var id = paymentSummary.id;

                if(paymentSummary.status == 'Sent'){
                    var conf = confirm(this.$t('form.validation.deleteSentPaymentSummary'));
                } else {
                    var conf = confirm(this.$t('form.validation.delete'));
                }

                if (conf === true) {
                    this.isLoading = true;
                    axios
                        .delete(`/api/payment-summaries/${id}`)
                        .then(response => {
                            this.isLoading = false;
                            const rowIndex = this.paymentSummaries.findIndex(item => item.id === id);
                            if (rowIndex !== -1) {
                                this.paymentSummaries.splice(rowIndex, 1);
                            }
                            this.showAlert(response.data.message);
                            this.getPaymentSummaries();
                        })
                        .catch(error => {
                            this.isLoading = false;
                            this.showAlert(error.response.data.message,'danger');

                        });
                }
            },

            getDate(date){
                var dateVar  = date?moment(date).format(this.dateFormat):moment().format(this.dateFormat)
                return dateVar;
            },
            /**
             * Build Query String for Filters
             */
            buildQueryString(obj) {
                let query = '';

                Object.keys(obj).forEach(function (key) {
                    if (obj[key] != '' && obj[key] != null) {
                        query += `${key}=${encodeURIComponent(obj[key])}&`;
                    }
                });

                if (this.pagination && this.pagination.current_page) {
                    query += `page=${this.pagination.current_page}`;
                    return query;
                }

                return query.slice(0, -1);
            },

            getCurrencies() {
                axios.get('/api/currencies').then(response => this.currencies = response.data);
            },

            async getPaymentSummaries() {

                this.isLoading = true;

                let filters = await this.buildQueryString(this.search);

                axios
                    .get(`/api/payment-summaries?status=${this.pending}&${filters}`, {
                        cancelToken: this.request_source.token
                    })
                    .then(response => {
                        this.isLoading = false;
console.log(response);
                        this.paymentSummaries = response.data.data.data;
                        this.pagination = response.data.pagination;
                    })
                    .catch(error => {
                        this.isLoading = false;
                        console.log(error);
                    });
            },

            paginate() {
                this.getPaymentSummaries();
            },

            searchPaymentSummary() {
                this.pagination.current_page = 1;
                this.getPaymentSummaries();
            },

            // Clear search input fields
            clearSearch() {
                this.search = {
                    studentNumber: '',
                    summary_date: '',
                    status: '',
                    reference_number: '',
                    total_amount: ''
                };

                this.searchPaymentSummary();
            },

            goToShow(id) {

                this.$router.push({
                    name: "PaymentSummaryShow",
                    params: {
                        id: id
                    },
                    query: {
                        origin: 'pending-payment-summaries'
                    }
                });
            },

            goToEdit(id) {

                this.$router.push({
                    name: 'PaymentSummaryEdit',
                    params: {
                        id: id
                    },
                    query: {
                        origin: 'pending-payment-summaries'
                    }
                });
            },

            toggleCheck(event){
            if (event.target.checked) {
                    this.selectedPaymentSummaries=this.paymentSummaries
                } else {
                    this.selectedPaymentSummaries=[]
                }
            },
            addToSendEmail(paymentSummary){
                if(!(this.selectedPaymentSummaries.includes(paymentSummary))){
                    this.selectedPaymentSummaries.push(paymentSummary)
                }
            },

            // Save a Preset with Unique Name
            savePreset() {
                this.isLoading = true;
                this.alert = false;

                this.preset.value = JSON.stringify(this.search);

                axios.post("/api/presets", this.preset).then(
                    response => {
                        this.isLoading = false;
                        this.showAlert(response.data.message);
                        this.getPresets();
                    },
                    error => {
                        this.isLoading = false;

                        if (error.response.status == 422) {
                            this.showAlert(error.response.data.errors.name[0], 'danger');
                        }
                    }
                );
            },

            // Get Presets in Search Criteria
            getPresets() {
                axios.get("/api/presets/?entity=" + this.preset.entity).then(
                    response => {
                        this.presetList = response.data;
                    },
                    error => {
                        console.log(error.response);
                    }
                );
            },

            // Get Search results according to selected Preset Name
            loadPreset() {
                if (!this.preset) {
                    this.preset = {
                        entity: "pendingPaymentSummaries"
                    };
                    this.clearSearch();
                    this.alert = false;
                    this.presetSelected = false;
                    return;
                }
                this.alert = false;
                this.presetSelected = true;
                this.search = JSON.parse(this.preset.value);
                this.searchPaymentSummary();
            },

            // Delete a selected Preset
            deletePreset() {
                let conf = confirm(this.$t('form.validation.delete'));

                if (conf === true) {
                    axios
                        .delete("/api/presets/" + this.preset.id)
                        .then(response => {
                            if (response.data.status == "success") {
                                this.showAlert(response.data.message);
                                this.getPresets();
                                this.clearSearch();
                                this.preset.name = "";
                                this.preset.value = {};
                                this.presetSelected = false;
                            }
                        })
                        .catch(error => {
                            this.isLoading = false;
                        });
                }
            },
            bulkApprove(){
            if(this.selectedPaymentSummaries.length>0){
                this.isLoading = true;
                axios
                    .post('/api/payment-summaries/bulk-approve',{
                        selectedPaymentSummaries:this.selectedPaymentSummaries
                    })
                    .then(response => {
                        this.isLoading = false;
                        this.selectedPaymentSummaries = []
                        this.getPaymentSummaries();

                        let xPaymentSummariesApproved = this.$t('form.paymentSummary.xPaymentSummariesApproved', {
                            X: response.data.data
                        });

                        this.showAlert(xPaymentSummariesApproved, 'success');
                    })
                    .catch(error => {
                        this.isLoading = true;
                        console.log('error');
                    });
                }else{
                this.showAlert(this.$t('form.paymentSummary.atLeastOneSelected'), 'danger');
                }
            },
            sendEmail(){
                if(this.selectedPaymentSummaries.length>0){
                this.isLoading = true;
                this.showAlert(this.$t('form.paymentSummary.waitToNotify'), 'primary');
                axios
                    .post('/api/students/invoices/notifications',{
                        selectedPaymentSummaries:this.selectedPaymentSummaries
                    })
                    .then(response => {
                        this.isLoading = false;
                        location.reload();
                    })
                    .catch(error => {
                        this.isLoading = true;
                        console.log('error');
                    });
                }else{
                this.showAlert(this.$t('form.paymentSummary.atLeastOneSelected'), 'danger');
                }
                
            },

            showAlert(msg, cls = 'success') {
                this.alertMessage = msg;
                this.alertClass = cls;
                this.alert = true;
            }
        }
    };

</script>